// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DropdownMenuItem.tsx
 * @output Exports DropdownMenuItem component
 * @position Sub-component; used inside DropdownMenu
 *
 * Interactive menu item with role="menuitem". Keyboard navigation
 * is handled by useListFocus on the parent menu container.
 *
 * Composes Item for the shared start content + label + description + end content layout.
 * Passes role="menuitem" so Item puts onClick on the root div instead of
 * creating an invisible button (keyboard access is provided by the parent menu).
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/DropdownMenu/DropdownMenu.doc.mjs
 * - /packages/core/src/DropdownMenu/DropdownMenuItem.doc.mjs
 * - /packages/core/src/DropdownMenu/DropdownMenu.test.tsx
 * - /packages/core/src/DropdownMenu/index.ts
 * - /apps/storybook/stories/DropdownMenu.stories.tsx
 * - /packages/cli/templates/blocks/components/DropdownMenu/ (showcase blocks)
 */
import { useCallback } from 'react';
import * as stylex from '@stylexjs/stylex';
import { renderIconSlot } from "../Icon/index.js";
import { Item } from "../Item/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { useDropdownMenuContext } from "./DropdownMenuContext.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
const menuItemStyles = {
  root: {
    kB7OPa: "x9f619",
    kzqmXN: "xh8yej3",
    k8WAf4: "xce4md1",
    kg3NbH: "xf314gf",
    kaIpWk: "x1ws5lxm",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w x1c52tdz x1uwx2nv",
    kkrTdU: "x1ypdohk",
    k9WMMc: "xdpxx8g",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
const itemSizeStyles = {
  sm: {
    k8WAf4: "xu0wf1k",
    kg3NbH: "xf314gf",
    $$css: true
  },
  md: {
    k8WAf4: "x1vofgu7",
    $$css: true
  },
  lg: {
    $$css: true
  }
};
/**
 * An interactive dropdown menu item with icon, label, and optional description.
 *
 * Must be used inside DropdownMenu. Keyboard navigation is provided
 * automatically by the parent via useListFocus.
 *
 * @example
 * ```
 * <DropdownMenu button={{ label: 'Actions' }}>
 *   <DropdownMenuItem icon={PencilIcon} label="Edit" onClick={handleEdit} />
 *   <DropdownMenuItem label="Delete" endContent={<Badge label="⌘D" />} onClick={handleDelete} />
 * </DropdownMenu>
 * ```
 */
export function DropdownMenuItem({
  icon,
  label,
  description,
  onClick,
  isDisabled = false,
  endContent,
  xstyle,
  className,
  style
}) {
  const ctx = useDropdownMenuContext();
  const menuSize = ctx?.menuSize ?? 'md';
  const handleClick = useCallback(() => {
    if (isDisabled || !onClick) {
      return;
    }
    onClick();
    ctx?.closeMenu();
  }, [isDisabled, onClick, ctx]);
  return /*#__PURE__*/_jsx(Item, {
    role: "menuitem",
    tabIndex: isDisabled ? undefined : -1,
    startContent: icon ? renderIconSlot(icon, {
      size: 'sm',
      color: 'secondary'
    }) : undefined,
    label: label,
    description: description,
    endContent: endContent,
    onClick: handleClick,
    isDisabled: isDisabled,
    xstyle: [menuItemStyles.root, itemSizeStyles[menuSize], isDisabled && menuItemStyles.disabled, xstyle],
    ...mergeProps(themeProps('dropdown-menu-item', {
      size: menuSize
    }), {
      className,
      style
    })
  });
}
DropdownMenuItem.displayName = 'DropdownMenuItem';